import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Loading } from "../../../components/Loading";
import VerificationReviewPanel from "../../../components/verification/VerificationReviewPanel";
import { getDriverDetails } from "../../../actions/inventory/driver.action";
import { DRIVER_OPERATIONAL_STATUS } from "../../../constants/driverStatus";

const accountStatusColorMap = {
  active: "green",
  inactive: "gray",
  suspended: "orange",
  blacklisted: "red",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveReviewStatus = (driver) => {
  if (driver?.isVerified || driver?.verificationStatus === "approved") return "approved";
  if (driver?.verificationStatus === "rejected") return "rejected";
  return "pending";
};

const getDocStatus = (documentation) => {
  if (!documentation) return "Missing";
  const dl = documentation?.drivingLicensePhotos || {};
  const aadhaar = documentation?.aadhaarCardPhotos || documentation?.adhaarCardPhotos || {};
  const pan = documentation?.panCardPhotos || {};
  const requiredFiles = [dl.front, dl.back, aadhaar.front, aadhaar.back, pan.front];
  return requiredFiles.every((value) => typeof value === "string" && value.trim()) ? "Complete" : "Incomplete";
};

const renderField = (label, value) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" color="gray.800" wordBreak="break-word">
      {value || "—"}
    </Text>
  </Box>
);

const renderDocumentPreview = (label, imageUrl) => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
    <Text fontSize="xs" textTransform="uppercase" color="gray.500">
      {label}
    </Text>
    {imageUrl ? (
      <>
        <Box mt={2} borderRadius="md" overflow="hidden" bg="gray.50">
          <Image src={imageUrl} alt={label} w="100%" h="170px" objectFit="cover" />
        </Box>
        <Button
          as="a"
          href={imageUrl}
          target="_blank"
          rel="noreferrer"
          mt={3}
          size="sm"
          variant="outline"
        >
          Open Full Image
        </Button>
      </>
    ) : (
      <Text mt={2} fontSize="sm" color="gray.500">
        Not uploaded
      </Text>
    )}
  </Box>
);

const DriverApprovalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const selectedRow = location?.state?.row || null;
  const [driverDetails, setDriverDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchDriverDetails = async () => {
      try {
        const response = await getDriverDetails(id);
        if (!mounted) return;
        setDriverDetails(response?.data?.data || response?.data || null);
      } catch (error) {
        if (!mounted) return;
        toast({
          title: "Failed to fetch driver details",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id && !selectedRow?.reviewOnly) {
      fetchDriverDetails();
    } else {
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [id, selectedRow?.reviewOnly, toast]);

  const resolvedData = useMemo(() => {
    const details = driverDetails || {};
    const documentation = details?.documentation || selectedRow?.documentation || {};
    const dl = documentation?.drivingLicensePhotos || {};
    const aadhaar = documentation?.aadhaarCardPhotos || documentation?.adhaarCardPhotos || {};
    const pan = documentation?.panCardPhotos || {};

    const accountStatus =
      details?.accountStatus || selectedRow?.accountStatus || "active";

    return {
      uniqueDriverId:
        details?.uniqueDriverId || selectedRow?.uniqueDriverId || "—",
      name: details?.name || selectedRow?.name || "—",
      email: details?.email || selectedRow?.email || "—",
      mobileNumber: details?.mobileNumber || selectedRow?.mobileNumber || "—",
      whatsappNumber: details?.whatsappNumber || selectedRow?.whatsappNumber || "—",
      dob: details?.dob || null,
      city: details?.city || selectedRow?.city || "—",
      state: details?.state || selectedRow?.state || "—",
      pincode: details?.pincode || "—",
      address: details?.address || "—",
      emergencyContactName: details?.emergencyContactName || "—",
      emergencyContactNumber: details?.emergencyContactNumber || "—",
      drivingLicenseNumber: details?.drivingLicenseNumber || "—",
      aadhaarCardNumber: details?.aadhaarCardNumber || "—",
      panCardNumber: details?.panCardNumber || "—",
      submittedAt:
        selectedRow?.submittedAt || details?.createdAt || details?.updatedAt || null,
      isVerified:
        typeof details?.isVerified === "boolean"
          ? details.isVerified
          : Boolean(selectedRow?.isVerified),
      operationalStatus:
        details?.operationalStatus ||
        selectedRow?.operationalStatus ||
        DRIVER_OPERATIONAL_STATUS.OFFLINE,
      accountStatus,
      verificationStatus:
        selectedRow?.verificationStatus || details?.verificationStatus || null,
      documentsStatus:
        selectedRow?.documentsStatus ||
        (details?.documentationAvailable ? "Complete" : getDocStatus(documentation)),
      drivingLicenseFront: details?.drivingLicenseFront || dl?.front || "",
      drivingLicenseBack: details?.drivingLicenseBack || dl?.back || "",
      aadhaarCardFront: details?.aadhaarCardFront || aadhaar?.front || "",
      aadhaarCardBack: details?.aadhaarCardBack || aadhaar?.back || "",
      panCardFront: details?.panCardFront || pan?.front || "",
      panCardBack: details?.panCardBack || pan?.back || "",
    };
  }, [driverDetails, selectedRow]);

  if (loading) return <Loading />;

  if (!selectedRow && !driverDetails) {
    return (
      <Stack p={6} gap={4}>
        <BreadCrumbMaker
          items={[
            { link: "/verification", title: "Verification Center" },
            { link: "/verification/driver-approvals", title: "Driver Approvals" },
            { link: "#", title: "Driver Details", on: true },
          ]}
        />
        <Box bg="white" borderRadius="xl" p={6} shadow="sm">
          <Text color="gray.600">Driver details not found.</Text>
          <Button mt={4} onClick={() => navigate("/verification/driver-approvals")}>
            Back to Driver Approvals
          </Button>
        </Box>
      </Stack>
    );
  }

  const reviewStatus =
    selectedRow?.reviewStatus ||
    deriveReviewStatus({
      isVerified: resolvedData.isVerified,
      accountStatus: resolvedData.accountStatus,
      verificationStatus: resolvedData.verificationStatus,
    });

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "/verification/driver-approvals", title: "Driver Approvals" },
          { link: "#", title: "Driver Details", on: true },
        ]}
      />

      <VerificationReviewPanel
        entityType="driver"
        entityId={selectedRow?.reviewOnly ? selectedRow?.entityId || null : selectedRow?.entityId || id}
      />

      <Box bg="white" borderRadius="xl" p={6} shadow="sm">
        <Flex justify="space-between" align="center" mb={5} gap={3} wrap="wrap">
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Driver Details
          </Text>
          {/* <Button variant="outline" onClick={() => navigate("/verification/driver-approvals")}>
            Back to List
          </Button> */}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField("Driver ID", resolvedData.uniqueDriverId)}
          {renderField("Name", resolvedData.name)}
          {renderField("Email", resolvedData.email)}
          {renderField("Mobile Number", resolvedData.mobileNumber)}
          {renderField("WhatsApp Number", resolvedData.whatsappNumber)}
          {renderField("Date of Birth", formatDate(resolvedData.dob))}
          {renderField("City", resolvedData.city)}
          {renderField("State", resolvedData.state)}
          {renderField("Pincode", resolvedData.pincode)}
          {renderField("Address", resolvedData.address)}
          {renderField("Emergency Contact", resolvedData.emergencyContactName)}
          {renderField("Emergency Number", resolvedData.emergencyContactNumber)}
          {renderField("License Number", resolvedData.drivingLicenseNumber)}
          {renderField("Aadhaar Number", resolvedData.aadhaarCardNumber)}
          {renderField("PAN Number", resolvedData.panCardNumber)}
          {renderField("Operational Status", formatLabel(resolvedData.operationalStatus))}
          {renderField("Account Status", formatLabel(resolvedData.accountStatus))}
        </SimpleGrid>

        <Divider my={5} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField("Submitted At", formatDate(resolvedData.submittedAt))}
          {renderField("Verification Status", formatLabel(reviewStatus))}
          {renderField("Documentation", resolvedData.documentsStatus)}
          <Box>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              Current Backend Status
            </Text>
            <Badge colorScheme={resolvedData.isVerified ? "green" : "yellow"}>
              {resolvedData.isVerified ? "Verified" : "Not Verified"}
            </Badge>
          </Box>
          <Box>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              Account Badge
            </Text>
            <Badge colorScheme={accountStatusColorMap[resolvedData.accountStatus] || "gray"}>
              {formatLabel(resolvedData.accountStatus)}
            </Badge>
          </Box>
        </SimpleGrid>

        <Divider my={5} />
        <Text fontSize="sm" fontWeight="semibold" mb={3}>
          Uploaded Documents
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderDocumentPreview("Driving License Front", resolvedData.drivingLicenseFront)}
          {renderDocumentPreview("Driving License Back", resolvedData.drivingLicenseBack)}
          {renderDocumentPreview("Aadhaar Front", resolvedData.aadhaarCardFront)}
          {renderDocumentPreview("Aadhaar Back", resolvedData.aadhaarCardBack)}
          {renderDocumentPreview("PAN Front", resolvedData.panCardFront)}
          {renderDocumentPreview("PAN Back", resolvedData.panCardBack)}
        </SimpleGrid>
      </Box>
    </Stack>
  );
};

export default DriverApprovalDetails;
